import { Card } from '@design/components/card/Card';
import { EntityCell } from '@terminal/components/entity-cell/EntityCell';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { TabsType } from '@terminal/modules/tab-content/types';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { DateCell } from '@terminal/widgets/earnings/components/date-cell/DateCell';
import { NumberCell } from '@terminal/widgets/earnings/components/number-cell/NumberCell';
import { PriceCell } from '@terminal/widgets/earnings/components/price-cell/PriceCell';
import { useTranslation } from 'react-i18next';

import styles from './EarningsReportingSummary.module.scss';
import { GuidanceCell } from './GuidanceCell';
import type { ReportedTodayRow, ReportingThisWeekRow } from './types';

type EarningsCompanyCardProps =
  | {
      row: ReportedTodayRow;
      variant: 'reported';
    }
  | {
      row: ReportingThisWeekRow;
      variant: 'upcoming';
    };

export const EarningsCompanyCard = (props: EarningsCompanyCardProps) => {
  const { row, variant } = props;
  const { t } = useTranslation(['widget']);
  const { navigateToPage } = useNavigateToAssetOverview();

  const handleClick = () => {
    navigateToPage({
      entityTag: row.entity_tag,
      source: POSTHOG_TRACKING_SOURCES.THEME_EARNINGS_DRAWER,
      tab: TabsType.Earnings,
    });
  };

  return (
    <Card size="small" className={styles.companyCard}>
      <button
        type="button"
        className={styles.companyCardButton}
        onClick={handleClick}
        data-testid={`${variant}-earnings-company-card`}
      >
        <EntityCell entityTag={row.entity_tag} />
        <div className={styles.companyMetrics}>
          {variant === 'reported' ? (
            <>
              <div className={styles.companyMetric}>
                <span>
                  {t(
                    'widget:themeOverview.tabs.earnings.reportingSummary.epsSurprisePct'
                  )}
                </span>
                <NumberCell value={row.eps.surprise_percentage} percentage />
              </div>
              <div className={styles.companyMetric}>
                <span>
                  {t(
                    'widget:themeOverview.tabs.earnings.reportingSummary.revenueSurprisePct'
                  )}
                </span>
                <NumberCell
                  value={row.revenue.surprise_percentage}
                  percentage
                />
              </div>
              <div className={styles.companyMetric}>
                <span>
                  {t(
                    'widget:themeOverview.tabs.earnings.reportingSummary.guidance'
                  )}
                </span>
                <GuidanceCell guidance={row.guidance} />
              </div>
            </>
          ) : (
            <>
              <div className={styles.companyMetric}>
                <span>
                  {t(
                    'widget:themeOverview.tabs.earnings.reportingSummary.expectedEps'
                  )}
                </span>
                <span className={styles.expectedValue}>
                  <NumberCell value={row.eps.estimate} variant="neutral" />
                </span>
              </div>
              <div className={styles.companyMetric}>
                <span>
                  {t(
                    'widget:themeOverview.tabs.earnings.reportingSummary.expectedRevenue'
                  )}
                </span>
                <span className={styles.expectedValue}>
                  <PriceCell value={row.revenue.estimate} />
                </span>
              </div>
              <div className={styles.companyMetric}>
                <span>{t('widget:earnings.scheduledDate')}</span>
                <span className={styles.scheduledDate}>
                  <DateCell date={new Date(row.reporting_date)} />
                </span>
              </div>
            </>
          )}
        </div>
      </button>
    </Card>
  );
};
